import PropTypes from 'prop-types';
import * as React from 'react';
import { ColorSwatchLabelPositions, ColorSwatchGradientTypes, useColorSwatchesContext } from './color-swatches.context.js';
import { createComponent } from '../core/create-component.js';
import { className } from '../../../core/utilities/classnames.utils.js';
import { VisuallyHidden } from '../visually-hidden/visually-hidden.component.js';

function _define_property(obj, key, value) {
  if (key in obj) {
    Object.defineProperty(obj, key, {
      value: value,
      enumerable: true,
      configurable: true,
      writable: true
    });
  } else {
    obj[key] = value;
  }

  return obj;
}

function _object_spread(target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = arguments[i] != null ? arguments[i] : {};
    var ownKeys = Object.keys(source);

    if (typeof Object.getOwnPropertySymbols === "function") {
      ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function (sym) {
        return Object.getOwnPropertyDescriptor(source, sym).enumerable;
      }));
    }

    ownKeys.forEach(function (key) {
      _define_property(target, key, source[key]);
    });
  }

  return target;
}

function ownKeys(object, enumerableOnly) {
  var keys = Object.keys(object);

  if (Object.getOwnPropertySymbols) {
    var symbols = Object.getOwnPropertySymbols(object);

    if (enumerableOnly) {
      symbols = symbols.filter(function (sym) {
        return Object.getOwnPropertyDescriptor(object, sym).enumerable;
      });
    }

    keys.push.apply(keys, symbols);
  }

  return keys;
}

function _object_spread_props(target, source) {
  source = source != null ? source : {};

  if (Object.getOwnPropertyDescriptors) {
    Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
  } else {
    ownKeys(Object(source)).forEach(function (key) {
      Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
    });
  }

  return target;
}

function _object_without_properties(source, excluded) {
  if (source == null) return {};

  var target = _object_without_properties_loose(source, excluded);

  var key, i;

  if (Object.getOwnPropertySymbols) {
    var sourceSymbolKeys = Object.getOwnPropertySymbols(source);

    for (i = 0; i < sourceSymbolKeys.length; i++) {
      key = sourceSymbolKeys[i];
      if (excluded.indexOf(key) >= 0) continue;
      if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue;
      target[key] = source[key];
    }
  }

  return target;
}

function _object_without_properties_loose(source, excluded) {
  if (source == null) return {};
  var target = {};
  var sourceKeys = Object.keys(source);
  var key, i;

  for (i = 0; i < sourceKeys.length; i++) {
    key = sourceKeys[i];
    if (excluded.indexOf(key) >= 0) continue;
    target[key] = source[key];
  }

  return target;
}

var propTypes = {
  /**
  * @deprecated
  * use accessibleText prop instead
  */
  title: PropTypes.string,

  /**
  * The main color of the swatch. Accepts a css color value
  */
  primaryColor: PropTypes.string,

  /**
  * The optional secondary color of the swatch. Accepts a css color value
  */
  secondaryColor: PropTypes.string,

  /**
  * URL for a background image for the swatch.
  */
  backgroundImage: PropTypes.string,

  /**
  * Whether or not the underlying input for the swatch should be enabled.
  */
  disabled: PropTypes.bool,

  /**
  * A text representation of the color value. Used in the hover tooltip and as a hidden label for accessibility
  */
  accessibleText: PropTypes.string,

  /**
  * Where to position the label tooltip relative to the swatch. Overrides the position setting of the ColorSwatches component.
  */
  labelPosition: PropTypes.oneOf(ColorSwatchLabelPositions),

  /**
  * Configures a gradient between the primary and secondary colors. By default shows the two colors stacked but separate
  */
  gradient: PropTypes.oneOf(ColorSwatchGradientTypes)
};
var propKeysToRemove = Object.keys(propTypes).filter(function (k) {
  return k !== 'title';
});
var ColorSwatch = createComponent({
  displayName: 'ColorSwatch',
  root: 'div',
  propTypes: propTypes,
  propKeysToRemove: propKeysToRemove,
  propConfig: {
    processProps: function processProps(_param) {
      var _param$style = _param.style,
          style = _param$style === void 0 ? {} : _param$style,
          primaryColor = _param.primaryColor,
          secondaryColor = _param.secondaryColor,
          accessibleText = _param.accessibleText,
          backgroundImage = _param.backgroundImage,
          title = _param.title,
          disabled = _param.disabled,
          props = _object_without_properties(_param, ["style", "primaryColor", "secondaryColor", "accessibleText", "backgroundImage", "title", "disabled"]);

      var ctx = useColorSwatchesContext();
      return _object_spread_props(_object_spread({
        labelPosition: ctx === null || ctx === void 0 ? void 0 : ctx.labelPosition
      }, props), {
        'aria-disabled': !!disabled,
        accessibleText: accessibleText !== null && accessibleText !== void 0 ? accessibleText : title,
        title: undefined,
        disabled: disabled,
        role: 'presentation',
        style: _object_spread_props(_object_spread({}, style), {
          backgroundColor: primaryColor !== null ? primaryColor : undefined,
          color: secondaryColor !== null && secondaryColor !== void 0 ? secondaryColor : undefined,
          backgroundImage: backgroundImage ? "url(".concat(backgroundImage, ")") : undefined
        })
      });
    },
    classNames: function classNames(props) {
      var classNameSet = new Set(['swan-color-swatch']);
      var disabled = props.disabled,
          gradient = props.gradient;
      if (disabled) classNameSet.add("swan-color-swatch-disabled");
      if (gradient) classNameSet.add("swan-color-swatch-".concat(gradient, "-gradient"));
      return classNameSet;
    },
    wrapRoot: function wrapRoot(_ref, _dp, element) {
      var accessibleText = _ref.accessibleText,
          labelPosition = _ref.labelPosition;
      if (accessibleText === null) return element;
      var labelClasses = className(['swan-color-swatch-accessible-label', labelPosition && labelPosition !== 'top' ? "swan-color-swatch-accessible-label-".concat(labelPosition) : undefined]);
      return /*#__PURE__*/React.createElement("span", {
        className: "swan-color-swatch-wrapper"
      }, element, /*#__PURE__*/React.createElement(VisuallyHidden, {
        className: labelClasses
      }, accessibleText));
    }
  }
});
ColorSwatch.defaultProps = {
  primaryColor: 'transparent',
  disabled: false
};
export { ColorSwatch };